import React, { useCallback, useEffect, useRef } from "react";
import { MIN_COLUMN_WIDTH } from "./columnWidths";

/**
 * The report table's header cell, with a drag grip on its right edge.
 *
 * Installed through Ant's `components={{ header: { cell: ResizableTitle } }}`, so
 * it receives the props Ant would have put on the <th> plus whatever the column's
 * `onHeaderCell` returned - which is where ReportGrid passes the four resize
 * props below. Those are pulled out of the spread deliberately: anything left in
 * would land on the DOM node and React would warn about an unknown attribute.
 *
 * ── Why hand-rolled rather than react-resizable ──
 * It is not installed, and this is the whole of it: a pointerdown, a pointermove
 * and a pointerup. Adding the dependency would also mean adding its stylesheet
 * and pinning a React-17-compatible version.
 *
 * ── Notes on the details that matter ──
 *   - The grip is `rg-resize-handle`, which src/utils/dragToScroll.js lists in its
 *     IGNORED_SELECTOR so grab-to-pan leaves presses on it alone. Without that,
 *     dragging a column edge would also pan the table.
 *
 *     It must NOT be called `react-resizable-handle`, which was the first attempt
 *     because dragToScroll already ignored that name: react-resizable ships a
 *     GLOBAL rule for it (node_modules/react-resizable/css/styles.css) forcing
 *     20x20 and painting a corner-bracket glyph, which react-grid-layout pulls in
 *     for the editor's widget handles. Every report header grew a small bracket in
 *     its top-right corner.
 *   - Pointer capture on the grip: the pointer routinely leaves the 8px strip
 *     mid-drag, and without capture the move events would go to whatever is
 *     under the cursor instead.
 *   - Moves are coalesced to one width update per animation frame. A pointermove
 *     can fire several times a frame and each one re-renders the table.
 *   - Double-click auto-fits, the way it does in a spreadsheet.
 */
export default function ResizableTitle(props) {
  const { resizeKey, resizeWidth, onResize, onResizeEnd, onAutoFit, children, ...thProps } = props;

  // { startX, startWidth, width } while a drag is in flight, else null
  const drag = useRef(null);
  const frame = useRef(null);

  const cancelFrame = useCallback(() => {
    if (frame.current !== null) {
      window.cancelAnimationFrame(frame.current);
      frame.current = null;
    }
  }, []);

  // A drag interrupted by an unmount (paging, a filter, a re-render of the board)
  // must not leave a frame queued against a dead component.
  useEffect(() => cancelFrame, [cancelFrame]);

  const handlePointerDown = useCallback(
    (event) => {
      if (event.button !== 0) return;
      // Stops the press from starting a text selection or a table pan.
      event.preventDefault();
      event.stopPropagation();

      const handle = event.currentTarget;
      const cell = handle.parentElement;
      /* Prefer the width we were told to draw. Reading it off the element is the
         fallback for the first drag of a column whose width has not resolved yet,
         and is also what makes the grip honest when the table has stretched its
         columns past their asked-for widths to fill the card. */
      const measured = cell ? cell.getBoundingClientRect().width : 0;
      const startWidth = Number(resizeWidth) > 0 ? Number(resizeWidth) : measured || MIN_COLUMN_WIDTH;

      drag.current = { startX: event.clientX, startWidth, width: startWidth };

      try {
        handle.setPointerCapture(event.pointerId);
      } catch (e) {
        /* capture is a nicety - the handlers still fire while the pointer is over the grip */
      }
    },
    [resizeWidth],
  );

  const handlePointerMove = useCallback(
    (event) => {
      if (!drag.current) return;

      const next = Math.max(MIN_COLUMN_WIDTH, Math.round(drag.current.startWidth + (event.clientX - drag.current.startX)));
      if (next === drag.current.width) return;
      drag.current.width = next;

      if (frame.current !== null) return; // a frame is already pending; it will read the latest
      frame.current = window.requestAnimationFrame(() => {
        frame.current = null;
        if (drag.current && typeof onResize === "function") onResize(resizeKey, drag.current.width);
      });
    },
    [onResize, resizeKey],
  );

  const handlePointerUp = useCallback(
    (event) => {
      if (!drag.current) return;

      const finalWidth = drag.current.width;
      drag.current = null;
      // Any queued frame would now read a null drag, so drop it and apply the
      // final width here instead - otherwise the last few pixels of the drag
      // could be discarded.
      cancelFrame();

      try {
        event.currentTarget.releasePointerCapture(event.pointerId);
      } catch (e) {
        /* released with the pointer */
      }

      if (typeof onResize === "function") onResize(resizeKey, finalWidth);
      // Only this fires the (persisting) callback - once per drag, not per frame.
      if (typeof onResizeEnd === "function") onResizeEnd(resizeKey, finalWidth);
    },
    [cancelFrame, onResize, onResizeEnd, resizeKey],
  );

  const handleDoubleClick = useCallback(
    (event) => {
      event.preventDefault();
      event.stopPropagation();
      if (typeof onAutoFit === "function") onAutoFit(resizeKey);
    },
    [onAutoFit, resizeKey],
  );

  // The scrollbar-lane filler cell and the expand column have no column behind
  // them, so they get a plain header cell.
  if (!resizeKey || typeof onResize !== "function") {
    return <th {...thProps}>{children}</th>;
  }

  return (
    <th {...thProps}>
      {/* The heading gets a box of its own for the same reason the values do: a
          centred heading too long for its column would otherwise hang off both
          sides of the cell and be clipped at the start with no ellipsis to show
          it. An inline-block that shrink-wraps and caps at the cell width reads
          correctly at either size - see reportGrid.css. It also keeps the text
          from running underneath the grip. */}
      <span className="rg-th-label">{children}</span>
      <span
        className="rg-resize-handle"
        role="separator"
        aria-orientation="vertical"
        aria-label="Resize column"
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerUp}
        onDoubleClick={handleDoubleClick}
        onClick={(event) => event.stopPropagation()}
      />
    </th>
  );
}
